/**
 * Bangladesh Income Tax calculator.
 * Implements: progressive slabs, category exemption, investment rebate (Sec 78),
 * minimum tax floor, high-net-worth surcharge, TDS reconciliation.
 */

import {
  TAX_YEARS,
  type AssesseeCategory,
  type Location,
  type Slab,
  type TaxYear,
} from "@/data/bdTaxSlabs";

export interface TaxInput {
  taxYearId: string;
  category: AssesseeCategory;
  location: Location;
  /** Total annual taxable income after any exempt salary components (BDT). */
  taxableIncome: number;
  /** Total qualifying investment for rebate (BDT). */
  investment: number;
  /** Net wealth (BDT) — used to determine surcharge. 0 disables. */
  netWealth: number;
  /** Total TDS already deducted (BDT). */
  tdsPaid: number;
}

export interface SlabBreakdown {
  /** 1-indexed slab number */
  index: number;
  rangeStart: number;
  rangeEnd: number; // Infinity for top slab
  rate: number; // marginal rate
  taxableInThisSlab: number;
  taxFromThisSlab: number;
}

export interface TaxResult {
  year: TaxYear;
  exemption: number;
  taxableAboveExemption: number;
  slabs: SlabBreakdown[];
  grossTax: number;
  rebate: number;
  rebateBase: number; // amount used to compute rebate (the min(...))
  taxAfterRebate: number;
  minimumTax: number;
  minimumTaxApplied: boolean;
  surchargeRate: number;
  surcharge: number;
  finalTax: number;
  tdsPaid: number;
  /** Positive => additional payment due. Negative => refund. */
  payableOrRefund: number;
  effectiveRate: number; // finalTax / taxableIncome
}

export function getTaxYear(id: string): TaxYear {
  return TAX_YEARS.find((y) => y.id === id) ?? TAX_YEARS[0];
}

export function getExemption(year: TaxYear, category: AssesseeCategory): number {
  return year.categoryExemption[category] ?? year.baseExemption;
}

/**
 * Apply slabs progressively to the income above the exemption.
 */
function applySlabs(slabs: Slab[], amount: number, exemption: number): SlabBreakdown[] {
  const rows: SlabBreakdown[] = [];
  // First row is the exemption itself (0%).
  rows.push({
    index: 1,
    rangeStart: 0,
    rangeEnd: exemption,
    rate: 0,
    taxableInThisSlab: Math.min(amount + exemption, exemption),
    taxFromThisSlab: 0,
  });

  let remaining = Math.max(0, amount);
  let cursor = exemption;
  let i = 2;
  for (const s of slabs) {
    const width = s.amount;
    const taxable = Math.min(remaining, width);
    const tax = taxable * s.rate;
    const end = isFinite(width) ? cursor + width : Infinity;
    rows.push({
      index: i++,
      rangeStart: cursor,
      rangeEnd: end,
      rate: s.rate,
      taxableInThisSlab: taxable,
      taxFromThisSlab: round2(tax),
    });
    remaining -= taxable;
    cursor = end;
    if (remaining <= 0 && isFinite(width)) break;
  }
  return rows;
}

function pickSurchargeRate(year: TaxYear, netWealth: number): number {
  let rate = 0;
  for (const b of year.surcharge) {
    if (netWealth > b.wealthAbove) rate = b.rate;
  }
  return rate;
}

export function computeTax(input: TaxInput): TaxResult {
  const year = getTaxYear(input.taxYearId);
  const exemption = getExemption(year, input.category);
  const taxable = Math.max(0, input.taxableIncome);
  const taxableAboveExemption = Math.max(0, taxable - exemption);

  const slabs = applySlabs(year.slabs, taxableAboveExemption, exemption);
  const grossTax = round2(slabs.reduce((s, r) => s + r.taxFromThisSlab, 0));

  // Rebate base = min(investment, pct% of taxable income, absoluteCap)
  const rebateBase = Math.min(
    Math.max(0, input.investment),
    taxable * year.rebate.pctOfTaxableIncome,
    year.rebate.absoluteCap,
  );
  const rebate = round2(rebateBase * year.rebate.rate);

  const afterRebate = Math.max(0, grossTax - rebate);

  // Minimum tax — only applicable when assessee has taxable income above exemption
  // (i.e., is required to be assessed). Below exemption => no income tax.
  const minTax = year.minimumTax[input.location];
  const minTaxApplies = taxableAboveExemption > 0;
  const taxAfterRebate = round2(afterRebate);
  const minimumTaxApplied = minTaxApplies && taxAfterRebate < minTax;
  const taxBeforeSurcharge = minTaxApplies ? Math.max(taxAfterRebate, minTax) : 0;

  const surchargeRate = pickSurchargeRate(year, Math.max(0, input.netWealth));
  const surcharge = round2(taxBeforeSurcharge * surchargeRate);

  const finalTax = round2(taxBeforeSurcharge + surcharge);
  const tdsPaid = Math.max(0, input.tdsPaid);
  const payableOrRefund = round2(finalTax - tdsPaid);
  const effectiveRate = taxable > 0 ? finalTax / taxable : 0;

  return {
    year,
    exemption,
    taxableAboveExemption,
    slabs,
    grossTax,
    rebate,
    rebateBase: round2(rebateBase),
    taxAfterRebate,
    minimumTax: minTax,
    minimumTaxApplied,
    surchargeRate,
    surcharge,
    finalTax,
    tdsPaid,
    payableOrRefund,
    effectiveRate,
  };
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}
